import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField, TextArea, SelectField } from "@/components/FormKit";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { code: "", name: "", description: "", parent_id: "none", status: "ativo" };

export default function CostCentersPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => { setLoading(true); api.get("/cost-centers").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    const payload = { ...form, parent_id: form.parent_id && form.parent_id !== "none" ? form.parent_id : null };
    try {
      if (editing) await api.put(`/cost-centers/${editing.id}`, payload);
      else await api.post("/cost-centers", payload);
      toast.success("Centro de custo salvo."); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const byId = useMemo(() => Object.fromEntries(rows.map((r) => [r.id, r])), [rows]);
  const parentOptions = useMemo(
    () => [{ value: "none", label: "Nenhum (centro principal)" }].concat(
      rows.filter((r) => r.id !== editing?.id).map((r) => ({ value: r.id, label: `${r.code} - ${r.name}` }))
    ), [rows, editing]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => [r.code, r.name].some((v) => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);
  const canEdit = hasPermission("cost_centers.update");

  return (
    <div className="space-y-6">
      <PageHeader title="Centros de Custo" subtitle="Áreas e setores do clube para classificação de despesas" />
      <Toolbar search={search} setSearch={setSearch} searchTestId="cc-search"
        onNew={hasPermission("cost_centers.create") ? () => { setEditing(null); setForm(EMPTY); setOpen(true); } : null}
        newLabel="Novo Centro de Custo" newTestId="add-cost-center-button" />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : filtered.length === 0 ? <EmptyState text="Nenhum centro de custo cadastrado." />
        : (
        <TableShell testId="cost-centers-table" headers={[{ label: "Código" }, { label: "Centro de Custo" }, { label: "Descrição" }, { label: "Centro Superior" }, { label: "Status" }, { label: "Ações", align: "right" }]}>
          {filtered.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`cc-row-${r.id}`}>
              <td className="px-4 py-3.5 font-mono text-slate-500">{r.code}</td>
              <td className="px-4 py-3.5 font-medium text-slate-800">{r.name}</td>
              <td className="px-4 py-3.5 text-slate-500">{r.description || "—"}</td>
              <td className="px-4 py-3.5 text-slate-500">{r.parent_id && byId[r.parent_id] ? `${byId[r.parent_id].code} - ${byId[r.parent_id].name}` : "—"}</td>
              <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5 text-right">
                {canEdit && <button onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r, parent_id: r.parent_id || "none" }); setOpen(true); }} data-testid={`edit-cc-${r.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <FormModal open={open} onOpenChange={setOpen} title={editing ? "Editar Centro de Custo" : "Novo Centro de Custo"} onSubmit={submit} submitting={submitting} testId="cc-form" width="max-w-lg">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField label="Código" value={form.code} onChange={(v) => set("code", v)} testId="cc-code" required />
          <TextField label="Nome" value={form.name} onChange={(v) => set("name", v)} testId="cc-name" required className="sm:col-span-2" />
          <TextArea label="Descrição" value={form.description} onChange={(v) => set("description", v)} testId="cc-description" className="sm:col-span-3" />
          <SelectField label="Centro Superior (opcional)" value={form.parent_id} onChange={(v) => set("parent_id", v)} testId="cc-parent" options={parentOptions} className="sm:col-span-2" />
          <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} testId="cc-status" options={[{ value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]} />
        </div>
      </FormModal>
    </div>
  );
}
